import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { BodyCompositionForm } from '@src/routes/members/components/body-measurements/form'
import { HistoryTable } from '@src/routes/members/components/body-measurements/table'
import { MetricData } from '@src/data/body-measurement'
import { Button } from '@src/elements/button/button'
import { BodyMeasurementsCard } from '@src/routes/members/components/body-measurements/body-measurements-card'

import './body-composition.sass'
import { WeightUnit } from '@src/util/weight'

@Component({
  computed: {
    weightUnit () {
      return this.$store.state.club.weightUnit
    }
  }
})
export class BodyComposition extends Vue {

  @Prop({ required: true })
  protected measurements: {
    trunkMuscleMass: MetricData[],
    trunkMusclePercentage: MetricData[],
    trunkFatMass: MetricData[],
    trunkFatPercentage: MetricData[],
    leftArmMuscleMass: MetricData[],
    leftArmMusclePercentage: MetricData[],
    leftArmFatMass: MetricData[],
    leftArmFatPercentage: MetricData[],
    rightArmMuscleMass: MetricData[],
    rightArmMusclePercentage: MetricData[],
    rightArmFatMass: MetricData[],
    rightArmFatPercentage: MetricData[],
    leftLegMuscleMass: MetricData[],
    leftLegMusclePercentage: MetricData[],
    leftLegFatMass: MetricData[],
    leftLegFatPercentage: MetricData[],
    rightLegMuscleMass: MetricData[],
    rightLegMusclePercentage: MetricData[],
    rightLegFatMass: MetricData[],
    rightLegFatPercentage: MetricData[]
  }

  protected weightUnit: WeightUnit
  protected groupedMeasurements: any[] = []
  protected selectedMeasurements: any[] = []

  private isCardExpanded: boolean = false

  constructor () {
    super()
  }

  expandCard () {
    this.isCardExpanded = !this.isCardExpanded
  }

  onSelectMeasurementsRow (selectedMeasurements: any[]) {
    this.selectedMeasurements = selectedMeasurements
  }

  render (h: CreateElement) {
    return <div class='measurements-section body-composition'>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION')}</Subtitle1>

      <div class='device-measurement-section-wrapper'>
        <div class='body-sections'>
          <div class='row'>
            {this.renderBodySection(h, 'trunk')}
          </div>
          <div class='row'>
            <div class='side-group'>
              {this.renderBodySection(h, 'left_Arm')}
              {this.renderBodySection(h, 'left_Leg')}
            </div>
            <div class='left-line' />
            <img class='body-icon side-group' src='/assets/icon/util/Bodysilhouette.svg' />
            <div class='right-line' />
            <div class='side-group'>
              {this.renderBodySection(h, 'right_Arm')}
              {this.renderBodySection(h, 'right_Leg')}
            </div>
          </div>
        </div>

        <div class='cards-header-row row'>
          <Button type='remove' click={this.expandCard} icon={this.isCardExpanded ? 'util/caret-up' : 'util/caret-down'}
                  label={this.$t(this.isCardExpanded ? 'COLLAPSE' : 'EXPAND')} ref='expandBtn' class='expand-btn' />
        </div>

        {this.isCardExpanded ? this.renderCard(h) : null}
        </div>
    </div>
  }

  renderBodySection (h: CreateElement, key: string) {
    const muscleMass = this.selectedMeasurements.filter(el => el.type === `${key.replace('_', '')}MuscleMass`)[0]
    const musclePercentage = this.selectedMeasurements.filter(el => el.type === `${key.replace('_', '')}MusclePercentage`)[0]
    const fatMass = this.selectedMeasurements.filter(el => el.type === `${key.replace('_', '')}FatMass`)[0]
    const fatPercentage = this.selectedMeasurements.filter(el => el.type === `${key.replace('_', '')}FatPercentage`)[0]

    return <div class={`body-section ${key}`}>
      <div class='muscle'>
        <Subtitle1 class='weight'>{muscleMass ? muscleMass.value : '-'}{this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)}</Subtitle1>
        <Subtitle1 class='percent'>{musclePercentage ? musclePercentage.value : '-'}%</Subtitle1>
        <Subtitle1 class='muscle-tag'>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.MUSCLE')}</Subtitle1>
      </div>
      <div class='fat'>
        <Subtitle1 class='weight'>{fatMass ? fatMass.value : '-'}{this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)}</Subtitle1>
        <Subtitle1 class='percent'>{fatPercentage ? fatPercentage.value : '-'}%</Subtitle1>
        <Subtitle1 class='fat-tag'>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.FAT')}</Subtitle1>
      </div>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.' + key.toUpperCase())}</Subtitle1>
    </div>
  }

  renderCard (h: CreateElement) {
    return <BodyMeasurementsCard type='bodyComposition'>
      <div slot='left'>
        <BodyCompositionForm />
      </div>
      <div slot='right'>
        <HistoryTable history={this.groupedMeasurements} title={this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.KNOWN_MEASUREMENTS')}
                      selectCallback={this.onSelectMeasurementsRow} selectedRowData={this.selectedMeasurements} type='bodyComposition' />
      </div>
    </BodyMeasurementsCard>
  }

  created () {
    this.formatHistory()
  }

  @Watch('measurements')
  onMeasurementsUpdated () {
    this.formatHistory()
  }

  protected formatHistory () {
    const measurements = this.getMeasurementsWithType()

    this.combineMeasurementsByDate(measurements)

    // show the first group of measurements in the muscle man initially
    this.selectedMeasurements = this.groupedMeasurements.length > 0 ? this.groupedMeasurements[0].measurements : []
  }

  private getMeasurementsWithType () {
    let measurements: any[] = []

    // set the type for each measurement
    Object.keys(this.measurements).forEach(key => {
      measurements.push(...this.measurements[key].map(entry => {
        return {
          ...entry,
          type: key
        }
      }))
    })

    return measurements
  }

  private combineMeasurementsByDate (measurements: any[]) {
    let measurementGroups = measurements.reduce((groupByDate, entry) => {
      if (!groupByDate[entry.effectiveAt]) {
        groupByDate[entry.effectiveAt] = { date: null, id: {}, measurements: [] }
      }
      groupByDate[entry.effectiveAt].effectiveAt = entry.effectiveAt
      groupByDate[entry.effectiveAt].id[entry.type] = entry.id
      groupByDate[entry.effectiveAt].measurements.push(entry)
      return groupByDate
    }, {})

    // covert the result object to a list
    this.groupedMeasurements = Object.values(measurementGroups)
  }
}
